//Imports React
import React, { useCallback, useContext, useEffect, useState } from "react";
import Select from 'react-select';
//Imports Contexts
import { AppContext } from "../../../../contexts/AppContext";
import { AuthContext } from "../../../../contexts/AuthContext";
//Imports Components
import ContainerSearchPMEReport from "./ContainerSearchReport";
import ComplianceIndicator from "../../../../shared/ComplianceReport";
import AnnualComplianceHolder from "./AnnualComplianceHolder";
import AnnualComplianceEstablishment from "./AnnualComplianceEstablishment";
import PrintExportButtons from "./PrintExportButtons";
//Imports Helpers
import { formatCurrency, getSubventionNames } from "../../../../shared/helpers";
//Imports Icons
import fileIcon from '../../../../assets/icons/reportsHover.svg';
//Imports CSS
import './pmeCumplianceReport.css';

function PMECumplianceReport() {
    const { dataProjects, establishmentList, pmeList } = useContext(AppContext);
    const { userData } = useContext(AuthContext);
    const [dataSearch, setDataSearch] = useState({
        region: null,
        commune: null,
        holder: { value: null, id: null },
        establishment: { value: null, id: null },
    });
    const [pme, setPme] = useState(null);
    const [view, setView] = useState(false);
    const [selectedYear, setSelectedYear] = useState(null);
    const [selectedEstablishment, setSelectedEstablishment] = useState(null);
    const [showAnnualComplianceHolder, setShowAnnualComplianceHolder] = useState(false);
    const [showAnnualComplianceEstablishment, setShowAnnualComplianceEstablishment] = useState(false);

    const isAdmin = userData?.permissions === 'admin';
    const isHolder = userData?.dimension === 'Sostenedor';
    const isEstablishment = userData?.dimension === 'Establecimiento';

    const getEstablishment = (establishmentId) => {
        return establishmentList.find(establishment => establishment.id === establishmentId);
    };

    const getFilteredPMEs = () => {
        let filtered = [...pme];
        // Filtrar por año si está seleccionado
        if (selectedYear) {
            filtered = filtered.filter(p => p.yearPME === selectedYear.value);
        }
        // Filtrar por establecimiento si está seleccionado
        if (selectedEstablishment) {
            filtered = filtered.filter(p => p.establishmentId === selectedEstablishment.value);
        }
        // Obtener años que tienen actividades después de filtrar
        const yearsWithActivities = [...new Set(filtered.map(p => p.yearPME))].sort((a, b) => b - a);

        return yearsWithActivities.map(year => {
            const pmesForYear = filtered.filter(p => p.yearPME === year);
            const activities = pmesForYear.flatMap(p => ({
                year,
                activities: p.activities.map(act => ({
                    ...act,
                    establishmentId: p.establishmentId
                }))
            }));
            return { year, activities: activities.flatMap(a => a.activities) };
        }).filter(yearGroup => yearGroup.activities.length > 0); // Solo retornar años que tienen actividades
    };


    const calculateTotals = (activities) => {
        return {
            totalSubsidyAmount: activities.reduce((sum, activity) => sum + (activity.totalSubsidyAmount || 0), 0),
            totalAccruedExpense: activities.reduce((sum, activity) => sum + (activity.accruedExpense || 0), 0),
            totalEffectiveExpense: activities.reduce((sum, activity) => sum + (activity.effectiveExpense || 0), 0),
            totalAccruedBalance: activities.reduce((sum, activity) => sum + (activity.accruedBalance || 0), 0),
            totalEffectiveBalance: activities.reduce((sum, activity) => sum + (activity.effectiveBalance || 0), 0)
        };
    };

    const updatePMEData = useCallback(() => {
        if (!pmeList || !establishmentList) return;

        if (isEstablishment) {
            // Usuario establecimiento: mostrar solo sus PMEs
            const establishmentPMEs = pmeList.filter(pme =>
                pme.establishmentId === userData.establishmentId
            );

            if (establishmentPMEs.length > 0) {
                setPme(establishmentPMEs);
                setView(true);
            }
        } else if (isHolder) {
            const establishments = establishmentList.filter(est => est.projectId === userData?.tenantId);
            const holderPMEs = pmeList.filter(pme =>
                establishments.some(est => est.id === pme.establishmentId)
            );
            if (holderPMEs.length > 0) {
                setPme(holderPMEs);
                setView(true);
            }

        } else if (userData?.permissions === 'admin') {
            if (dataSearch.holder.id) {
                const establishments = establishmentList.filter(est => est.projectId === dataSearch.holder.id);
                const holderPMEs = pmeList.filter(pme =>
                    establishments.some(est => est.id === pme.establishmentId)
                );
                if (holderPMEs.length > 0) {
                    setPme(holderPMEs);
                    setView(true);
                }
            }
        }
    }, [pmeList, establishmentList, userData, dataSearch?.holder?.id, isEstablishment, isHolder]);


    useEffect(() => {
        if (userData) {
            updatePMEData();
        }
    }, [updatePMEData, userData]);

    const handleSelect = (selectedOption, { name }) => {
        const value = selectedOption ? selectedOption.value : null;
        const id = selectedOption ? selectedOption.id : null;

        setDataSearch(prevState => ({
            ...prevState,
            [name]: name === 'holder'
                ? { value, id }
                : value,
            ...(name === 'region' && { commune: null, holder: { value: null, id: null }, establishment: null }),
            ...(name === 'commune' && { holder: { value: null, id: null }, establishment: null }),
            ...(name === 'holder' && { establishment: null })
        }));
        setView(false);
        setSelectedYear(null);
        setSelectedEstablishment(null);
    };
    const handleButtonClick = () => {
        updatePMEData()
    };

    const handleComplianceButtonClick = (type) => {
        if (type === 'holder') {
            setShowAnnualComplianceHolder(true);
            setShowAnnualComplianceEstablishment(false);
        } else if (type === 'establishment') {
            setShowAnnualComplianceHolder(false);
            setShowAnnualComplianceEstablishment(true);
        }
    };

    const renderMonitoreoTable = ({ year, activities }) => {
        const totals = calculateTotals(activities);
        return (
            <span key={year} className='year-section flex'>
                <p>PME Año {year}</p>
                <table className="monitoreo-table">
                    <thead>
                        <tr>
                            <th>Escuela</th>
                            <th>Subvención</th>
                            <th>Acción</th>
                            <th>Monto</th>
                            <th>Gasto Devengado</th>
                            <th>Gasto Efectivo</th>
                            <th>Saldo Devengado</th>
                            <th>Saldo Efectivo</th>
                            <th>% Cumplimiento</th>
                        </tr>
                    </thead>
                    <tbody>
                        {activities?.map(activity => (
                            <tr key={activity.id}>
                                <td>{getEstablishment(activity.establishmentId)?.rbd}</td>
                                <td>{getSubventionNames(activity.subsidies)}</td>
                                <td>{activity.nameOfAction}</td>
                                <td>{formatCurrency(activity.totalSubsidyAmount)}</td>
                                <td>{formatCurrency(activity.accruedExpense)}</td>
                                <td>{formatCurrency(activity.accruedBalance)}</td>
                                <td>{formatCurrency(activity.effectiveExpense)}</td>
                                <td>{formatCurrency(activity.effectiveBalance)}</td>
                                <td>
                                    <ComplianceIndicator
                                        percentage={(activity.accruedBalance * 100) / activity.totalSubsidyAmount}
                                    />
                                </td>
                            </tr>
                        ))}
                        <tr className="totals-row">
                            <td colSpan="3">Totales</td>
                            <td>{formatCurrency(totals.totalSubsidyAmount)}</td>
                            <td>{formatCurrency(totals.totalAccruedExpense)}</td>
                            <td>{formatCurrency(totals.totalAccruedBalance)}</td>
                            <td>{formatCurrency(totals.totalEffectiveExpense)}</td>
                            <td>{formatCurrency(totals.totalEffectiveBalance)}</td>
                            <td>
                                <ComplianceIndicator
                                    percentage={(totals.totalAccruedBalance * 100) / totals.totalSubsidyAmount}
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </span>
        )
    };
    return (
        <section className='pme-cumpliance-report flex'>
            {isAdmin && (
                <ContainerSearchPMEReport
                    dataSearch={dataSearch}
                    setDataSearch={setDataSearch}
                    handleSelect={handleSelect}
                    handleButtonClick={handleButtonClick}
                    dataProjects={dataProjects}
                    establishmentList={establishmentList}
                />
            )}
            <section className='report-containers flex'>
                {(view && pme) ? (
                    <>
                        <article className='info-establishment flex'>
                            <h3>Informes Cumplimiento PME</h3>
                            <span className='flex'>
                                <Select
                                    isClearable
                                    placeholder="Todos los años"
                                    value={selectedYear}
                                    onChange={setSelectedYear}
                                    options={[...new Set(pme.map(p => p.yearPME))]
                                        .filter(year => {
                                            // Si hay un establecimiento seleccionado, filtrar por años de ese establecimiento
                                            if (selectedEstablishment) {
                                                return pme.some(p =>
                                                    p.yearPME === year &&
                                                    p.establishmentId === selectedEstablishment.value
                                                );
                                            }
                                            return true;
                                        })
                                        .map(year => ({
                                            value: year,
                                            label: `PME ${year}`
                                        }))
                                    }
                                    className="custom-select"
                                    classNamePrefix='custom-select'
                                />
                                {(isHolder || userData?.permissions === 'admin') && (
                                    <Select
                                        isClearable
                                        placeholder="Todos los establecimientos"
                                        value={selectedEstablishment}
                                        onChange={setSelectedEstablishment}
                                        options={establishmentList
                                            .filter(est => est.projectId === (isHolder ? userData.tenantId : dataSearch.holder.id))
                                            .filter(est => pme.some(p =>
                                                p.establishmentId === est.id &&
                                                // Si hay un año seleccionado, filtrar por ese año
                                                (!selectedYear || p.yearPME === selectedYear.value)
                                            ))
                                            .map(est => ({
                                                value: est.id,
                                                label: est.nameEstablishment,
                                            }))}
                                        className='custom-select'
                                        classNamePrefix='custom-select'
                                    />
                                )}
                            </span>
                        </article>
                        <article className='pme-list flex'>
                            {getFilteredPMEs().map(yearGroup => renderMonitoreoTable(yearGroup))}
                        </article>
                        <article className='actions-buttons flex'>
                            {(isHolder || isAdmin) && (
                                <>
                                    <button className='btn-inform flex' onClick={() => handleComplianceButtonClick('holder')}>
                                        <img src={fileIcon} className='icon' alt='Icono de Archivo' />
                                        <p>Ver Cumplimiento Anual por Sostenedor</p>
                                    </button>
                                    <button className='btn-inform flex' onClick={() => handleComplianceButtonClick('establishment')}>
                                        <img src={fileIcon} className='icon' alt='Icono de Archivo' />
                                        <p>Ver Cumplimiento Anual por Establecimiento</p>
                                    </button>
                                </>
                            )}
                            <PrintExportButtons />
                        </article>
                    </>
                ) : (
                    <p className='text-bold message-not-found flex'>
                        {isEstablishment
                            ? 'No existen PMEs para este establecimiento'
                            : isHolder
                                ? 'No existen PMEs para los establecimientos a cargo'
                                : 'Seleccione un sostenedor para ver los PMEs'
                        }
                    </p>
                )}

            </section>
            {showAnnualComplianceHolder && (
                <>
                    <span className='modal-overlay'></span>
                    <AnnualComplianceHolder
                        selectedHolder={isAdmin ? dataSearch.holder : userData.tenantId}
                        setClose={setShowAnnualComplianceHolder}
                    />
                </>
            )
            }
            {showAnnualComplianceEstablishment && (
                <>
                    <span className='modal-overlay'></span>
                    <AnnualComplianceEstablishment
                        selectedHolder={isAdmin ? dataSearch.holder : userData.tenantId}
                        setClose={setShowAnnualComplianceEstablishment}
                    />
                </>
            )}
        </section>
    );

};

export default PMECumplianceReport;